import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileUp, Sparkles, Loader2, PenLine } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { OrderForm, EMPTY_ORDER } from "@/components/admin/OrderForm";

function PdfDrop({ onFile, onManual }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div className="space-y-4">
      <div
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
        data-testid="pdf-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-20 text-center transition-colors ${over ? "border-blue-500 bg-blue-500/10" : "border-white/15 bg-[#111827] hover:border-blue-500/60"}`}
      >
        <FileUp className="h-10 w-10 text-blue-400" />
        <div className="mt-4 font-display text-2xl font-bold uppercase">Trage aici PDF-ul comenzii</div>
        <p className="mt-2 max-w-md text-sm text-slate-400">Timocom, Trans.eu, curier sau orice ordin de transport. AI-ul extrage adresele de încărcare și descărcare; prețul rămâne doar la birou.</p>
        <input ref={ref} type="file" accept="application/pdf,.pdf" hidden data-testid="pdf-file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
      </div>
      <button type="button" onClick={onManual} data-testid="manual-order-btn" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
        <PenLine className="h-4 w-4" /> Sau completează manual, fără PDF
      </button>
    </div>
  );
}

export default function NewOrder() {
  const [phase, setPhase] = useState("upload");
  const [order, setOrder] = useState(EMPTY_ORDER);
  const [drivers, setDrivers] = useState([]);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, []);

  const onFile = async (file) => {
    setFileName(file.name);
    setPhase("parsing");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/orders/parse-pdf", fd);
      const ex = data.extracted;
      if (ex) {
        setOrder({ ...EMPTY_ORDER, ...ex, stops: ex.stops?.length ? ex.stops : EMPTY_ORDER.stops, pdf_file_id: data.file.id });
        toast.success("PDF citit. Verifică datele și alege șoferul.");
      } else {
        setOrder({ ...EMPTY_ORDER, pdf_file_id: data.file.id });
        toast.error(data.error || "Nu am putut citi PDF-ul. Completează manual.");
      }
      setPhase("form");
    } catch (e) {
      toast.error(errMsg(e));
      setPhase("upload");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/orders", order);
      toast.success("Comandă salvată");
      nav(`/admin/orders/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="eyebrow">Comandă nouă</div>
      <h1 className="mt-1 font-display text-4xl font-extrabold uppercase sm:text-5xl">Din PDF la șofer</h1>
      <div className="mt-8">
        {phase === "upload" && <PdfDrop onFile={onFile} onManual={() => setPhase("form")} />}
        {phase === "parsing" && (
          <div className="panel flex flex-col items-center py-20 text-center" data-testid="pdf-parsing">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
              <Sparkles className="absolute -right-2 -top-2 h-5 w-5 text-blue-300" />
            </div>
            <div className="mt-5 font-display text-2xl font-bold uppercase">AI citește documentul...</div>
            <p className="mt-1 font-mono text-xs text-slate-500">{fileName}</p>
          </div>
        )}
        {phase === "form" && (
          <>
            {fileName && <p className="mb-4 text-sm text-slate-400" data-testid="parsed-file-name">Extras din <span className="font-mono text-slate-200">{fileName}</span>. Verifică totul înainte de salvare.</p>}
            <OrderForm value={order} onChange={setOrder} drivers={drivers} />
            <div className="sticky bottom-0 mt-6 flex justify-end gap-3 border-t border-white/10 bg-[#0A0E17]/90 py-4 backdrop-blur">
              <button type="button" onClick={() => { setOrder(EMPTY_ORDER); setFileName(""); setPhase("upload"); }} data-testid="cancel-new-order-btn" className="rounded-full px-5 py-3 text-slate-300 hover:bg-white/5">Renunță</button>
              <button type="button" onClick={save} disabled={saving} data-testid="save-order-btn" className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salvează comanda
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
